import { Box } from "@mui/material";
import ReactECharts from "echarts-for-react";
import { useMemo } from "react";
import type {
	ForecastMode,
	ForecastReportMonth,
} from "../models/ForecastReport";

const MONTH_LABELS = [
	"Jan",
	"Feb",
	"Mar",
	"Apr",
	"May",
	"Jun",
	"Jul",
	"Aug",
	"Sep",
	"Oct",
	"Nov",
	"Dec",
];

type SeriesSpec = {
	name: string;
	color: string;
	data: (number | null)[];
	dashed: boolean;
	endLabel?: string;
};

const buildYearArrays = (
	year: number,
	rows: ForecastReportMonth[],
): { actual: (number | null)[]; forecast: (number | null)[] } => {
	const actual: (number | null)[] = Array(12).fill(null);
	const forecast: (number | null)[] = Array(12).fill(null);
	for (const r of rows) {
		if (r.Year !== year) continue;
		const idx = r.Number - 1;
		if (idx < 0 || idx > 11) continue;
		actual[idx] = r.ActualProduction;
		forecast[idx] = r.ForecastedProduction;
	}
	return { actual, forecast };
};

const buildMixedTransition = (
	actual: (number | null)[],
	forecast: (number | null)[],
): (number | null)[] => {
	let lastActualIdx = -1;
	for (let i = 11; i >= 0; i--) {
		if (actual[i] !== null) {
			lastActualIdx = i;
			break;
		}
	}
	if (lastActualIdx < 0 || lastActualIdx >= 11) return Array(12).fill(null);

	let nextForecastIdx = -1;
	for (let i = lastActualIdx + 1; i <= 11; i++) {
		if (forecast[i] !== null) {
			nextForecastIdx = i;
			break;
		}
	}
	if (nextForecastIdx < 0) return Array(12).fill(null);

	const out: (number | null)[] = Array(12).fill(null);
	out[lastActualIdx] = actual[lastActualIdx];
	out[nextForecastIdx] = forecast[nextForecastIdx];
	return out;
};

interface Props {
	mode: ForecastMode;
	visibleYears: Set<number>;
	rows: ForecastReportMonth[];
	colorForYear: (year: number) => string;
}

export const ForecastReportChart = ({
	mode,
	visibleYears,
	rows,
	colorForYear,
}: Props) => {
	const option = useMemo(() => {
		const currentYear = new Date().getFullYear();
		const years = Array.from(visibleYears).sort((a, b) => a - b);

		const series: SeriesSpec[] = [];

		for (const y of years) {
			const color = colorForYear(y);
			const { actual, forecast } = buildYearArrays(y, rows);

			if (mode === "Actual" || mode === "Mixed") {
				series.push({
					name: `Actual ${y}`,
					color,
					data: actual,
					dashed: false,
					endLabel: `Actual ${y}`,
				});
			}
			if (mode === "Forecast" || mode === "Mixed") {
				series.push({
					name: `Forecast ${y}`,
					color,
					data: forecast,
					dashed: true,
					endLabel: `Forecast ${y}`,
				});
			}
			if (mode === "Mixed" && y === currentYear) {
				const transition = buildMixedTransition(actual, forecast);
				if (transition.some((v) => v !== null)) {
					series.push({
						name: `Transition ${y}`,
						color,
						data: transition,
						dashed: true,
					});
				}
			}
		}

		return {
			animation: true,
			animationDuration: 0,
			animationDurationUpdate: 400,
			animationEasingUpdate: "cubicOut",
			tooltip: {
				trigger: "axis" as const,
				valueFormatter: (v: number | null) =>
					v == null
						? "—"
						: new Intl.NumberFormat("en-US", {
								minimumFractionDigits: 2,
								maximumFractionDigits: 2,
							}).format(Number(v)),
			},
			legend: { show: false },
			grid: { left: 60, right: 60, top: 16, bottom: 32 },
			xAxis: {
				type: "category" as const,
				data: MONTH_LABELS,
				axisLabel: { fontSize: 11 },
			},
			yAxis: {
				type: "value" as const,
				axisLabel: {
					formatter: (v: number) =>
						v >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(v),
				},
			},
			series: series.map((s) => ({
				name: s.name,
				type: "line" as const,
				data: s.data,
				itemStyle: { color: s.color },
				lineStyle: {
					color: s.color,
					width: 2,
					type: s.dashed ? ("dashed" as const) : ("solid" as const),
				},
				symbolSize: 7,
				connectNulls: true,
				endLabel: s.endLabel
					? {
							show: true,
							formatter: s.endLabel,
							color: s.color,
							fontSize: 11,
							offset: [8, 0],
						}
					: undefined,
			})),
		};
	}, [mode, visibleYears, rows, colorForYear]);

	return (
		<Box sx={{ width: "100%", height: "100%" }}>
			<ReactECharts
				option={option}
				notMerge={true}
				lazyUpdate
				style={{ width: "100%", height: "100%" }}
			/>
		</Box>
	);
};
